<script setup lang="ts">
import { $customEventRoleList } from '@/api/users'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: any,
  label?: string,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => {},
  label: 'Event Role'
})

const emit = defineEmits<{
  'update:modelValue': (newValue: any) => void
}>()

interface CustomEventRole {
  id: number
  name: string
}

const eventRoles = ref<CustomEventRole[]>([])
const eventRolesCurrentPage = ref<number>(1)
const eventRolesPerPage: number = 10
const eventRolesSearchStr = ref<string>('')
const isNextEventRolesPageExist = ref<boolean>(false)
const eventRolesLoading = ref<boolean>(false)


const getEventRoles = async () => {
  eventRolesLoading.value = true
  try {
    const res = await $customEventRoleList({
      search: eventRolesSearchStr.value,
      per_page: eventRolesPerPage,
      page: eventRolesCurrentPage.value,
    })

    // Append the new eventRoles to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    eventRoles.value = uniqBy([...eventRoles.value, ...res.data.results], 'id')
    isNextEventRolesPageExist.value = !!res.data.next
  }
  catch (e) {
    console.log(e)
  }
  finally {
    eventRolesLoading.value = false
  }
}

const onFocusEventRoleSelect = async () => {
  if (!eventRoles.value.length && !props.modelValue) {
    eventRoles.value = []
    eventRolesCurrentPage.value = 1
    await getEventRoles()
  }
}

const onLoadMoreEventRoles = async () => {
  eventRolesCurrentPage.value++ // Increment the current page to load the next page of eventRoles
  await getEventRoles()
}

const handleEventRoleSearch = useDebounceFn((searchStr: string) => {
  eventRoles.value = []
  eventRolesCurrentPage.value = 1 // Set current page => 1 to load initial first page of eventRoles
  eventRolesSearchStr.value = searchStr
  getEventRoles()
}, 500)
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...eventRoles]"
    return-object
    item-title="name"
    item-value="id"
    :label="label"
    density="compact"
    no-filter
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusEventRoleSelect"
    @update:search="handleEventRoleSearch"
  >
    <template #no-data>
      <div
        v-if="!!eventRolesLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Event Roles not found
      </div>
    </template>
    <template
      v-if="isNextEventRolesPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!eventRolesLoading"
        @click="onLoadMoreEventRoles"
      >
        Load more...
        <VProgressCircular
          v-if="!!eventRolesLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>
